/**
 * IDE onboarding card (REQ-GW-015).
 *
 * Shows the DR-scoped endpoint, public aliases and copy-ready snippets. The
 * API key is never embedded: clients read it from their own secret store.
 */

import { useMemo, useState } from 'react';
import { Check, Copy, Laptop, Terminal } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useI18n } from '@/i18n';
import { useGatewayConnectConfig } from '../hooks/useGateway';
import type { GatewayConnectConfig } from '../types';

type Client = 'vscode' | 'opencode' | 'continue' | 'curl';

const CLIENTS: Client[] = ['vscode', 'opencode', 'continue', 'curl'];

function chatModels(config: GatewayConnectConfig) {
  return config.models.filter((model) => model.kind === 'chat');
}

function vscodeSnippet(config: GatewayConnectConfig): string {
  const models = chatModels(config);
  return JSON.stringify(
    [
      {
        name: 'Klviar',
        vendor: 'customendpoint',
        apiType: 'chat-completions',
        apiKey: '${input:klviarApiKey}',
        models: models.map((model) => ({
          id: model.id,
          name: model.name,
          url: config.chat_completions_url,
          toolCalling: true,
          vision: false,
          maxInputTokens: 128000,
          maxOutputTokens: 16000,
        })),
      },
    ],
    null,
    2,
  );
}

function opencodeSnippet(config: GatewayConnectConfig): string {
  const models = chatModels(config);
  const primary = models[0]?.id ?? config.models[0]?.id ?? 'claude-haiku-4-5';
  const fast = models.find((model) => model.id === 'claude-haiku-4-5')?.id ?? primary;
  return JSON.stringify(
    {
      $schema: 'https://opencode.ai/config.json',
      provider: {
        klviar: {
          npm: '@ai-sdk/openai-compatible',
          name: 'Klviar',
          options: {
            baseURL: config.openai_base_url,
            apiKey: '{env:KLVIAR_API_KEY}',
          },
          models: Object.fromEntries(models.map((model) => [model.id, { name: model.name }])),
        },
      },
      model: `klviar/${primary}`,
      small_model: `klviar/${fast}`,
    },
    null,
    2,
  );
}

function continueSnippet(config: GatewayConnectConfig): string {
  const models = chatModels(config);
  const primary = models[0] ?? { id: 'claude-sonnet-4-5', name: 'Claude Sonnet 4.5' };
  return [
    'name: Klviar',
    'version: 0.0.1',
    'schema: v1',
    'models:',
    `  - name: ${primary.name}`,
    '    provider: openai',
    `    model: ${primary.id}`,
    `    apiBase: ${config.openai_base_url}`,
    '    apiKey: ${{ secrets.KLVIAR_API_KEY }}',
    '    roles:',
    '      - chat',
    '      - edit',
    '      - apply',
  ].join('\n');
}

function curlSnippet(config: GatewayConnectConfig): string {
  const primary = chatModels(config)[0]?.id ?? 'claude-haiku-4-5';
  return [
    `curl ${config.chat_completions_url} \\`,
    '  -H "Authorization: Bearer $KLVIAR_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '{"model":"${primary}","messages":[{"role":"user","content":"ping"}]}'`,
  ].join('\n');
}

function snippetFor(client: Client, config: GatewayConnectConfig): string {
  if (client === 'vscode') return vscodeSnippet(config);
  if (client === 'opencode') return opencodeSnippet(config);
  if (client === 'continue') return continueSnippet(config);
  return curlSnippet(config);
}

export function ConnectIdeCard() {
  const { t } = useI18n();
  const query = useGatewayConnectConfig();
  const [client, setClient] = useState<Client>('vscode');
  const [copied, setCopied] = useState(false);

  const snippet = useMemo(
    () => (query.data ? snippetFor(client, query.data) : ''),
    [client, query.data],
  );

  async function copySnippet() {
    if (!snippet) return;
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Card data-testid="gateway-connect-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Laptop className="h-5 w-5 text-primary" aria-hidden="true" />
          {t('gateway.connect.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('gateway.connect.subtitle')}</p>

        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">{t('gateway.connect.loading')}</p>
        ) : query.isError || !query.data ? (
          <p className="text-sm text-red-600" role="alert">
            {t('gateway.connect.error')}
          </p>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('gateway.connect.endpoint')}
                </p>
                <code className="mt-1 block break-all rounded bg-muted px-2 py-1 font-mono text-xs">
                  {query.data.openai_base_url}
                </code>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('gateway.connect.scopes')}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {query.data.recommended_scopes.map((scope) => (
                    <span
                      key={scope}
                      className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
                    >
                      {scope}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('gateway.connect.models')}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {query.data.models.map((model) => (
                  <span
                    key={model.id}
                    className="rounded border px-1.5 py-0.5 font-mono text-xs"
                  >
                    {model.id}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2" role="tablist">
              {CLIENTS.map((item) => (
                <Button
                  key={item}
                  variant={client === item ? 'default' : 'outline'}
                  size="sm"
                  role="tab"
                  aria-selected={client === item}
                  onClick={() => setClient(item)}
                  data-testid={`connect-client-${item}`}
                >
                  {t(`gateway.connect.clients.${item}`)}
                </Button>
              ))}
            </div>

            <div className="relative">
              <pre
                className="max-h-80 overflow-auto rounded bg-muted p-3 font-mono text-xs"
                data-testid="connect-snippet"
              >
                {snippet}
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="absolute right-2 top-2"
                onClick={() => void copySnippet()}
                data-testid="connect-copy"
              >
                {copied ? (
                  <Check className="h-3 w-3 mr-1" aria-hidden="true" />
                ) : (
                  <Copy className="h-3 w-3 mr-1" aria-hidden="true" />
                )}
                {copied ? t('gateway.connect.copied') : t('gateway.connect.copy')}
              </Button>
            </div>

            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Terminal className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              {t(`gateway.connect.hints.${client}`)}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
